import {
  ActionIcon,
  Box,
  Card,
  Divider,
  Flex,
  Grid,
  Image,
  Modal,
  Stack,
  Text,
  createStyles,
} from "@mantine/core";
import { IconArrowLeft, IconCalendar, IconMapPin } from "@tabler/icons-react";
import { get } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchPromotionQuery } from "@/services/authAPI";
import imgKmFallback from "@/assets/images/pngs/km1.png";
// Icon thẻ khuyến mãi — Figma Huge-icon (lưu file riêng, import dạng ReactComponent)
import { ReactComponent as CalendarCheckIcon } from "./icons/calendar-check.svg";
import { ReactComponent as Map02Icon } from "./icons/map-02.svg";

interface Props {
  opened: boolean;
  close: () => void;
}

export type Promotion = {
  pmId: string;
  title: string;
  imageRepresent: string;
  startDate: number | string;
  endDate: string;
  merchantId: string;
  merchantName: string;
  source: string;
  locationName?: string;
  shortContent?: string;
  content?: string; // Mô tả chung
  guide?: string; // Hướng dẫn sử dụng — các ý phân cách bằng '*'
  /**
   * #56358/#56359: `searchPromotion` ĐÃ trả sẵn bản tiếng Anh (kiểm chứng trên dev 17/09/2026,
   * 6/6 bài đều có đủ) nhưng web chưa khai nên bỏ qua hoàn toàn — bài viết luôn ra tiếng Việt.
   * Ảnh cũng có bản riêng vì ảnh khuyến mại có chữ nằm trong ảnh.
   */
  titleEn?: string;
  shortContentEn?: string;
  contentEn?: string;
  imageRepresentEn?: string;
  /**
   * Hai trường này BE bổ sung sau (#56359): `locationNameEn` lấy từ BRANCHS.BRANCH_NAME_EN,
   * `guideEn` từ MERCHANT_PROMOTION_NEWS.MOBILE_GUIDE_EN. Truy vấn đã lùi về bản tiếng Việt
   * nếu chưa dịch nên không bao giờ trống.
   */
  locationNameEn?: string;
  guideEn?: string;
};

/**
 * Chọn bản tiếng Anh khi đang ở tiếng Anh; bài nào chưa dịch thì lùi về tiếng Việt
 * (thà hiện tiếng Việt còn hơn để trống).
 *
 * Nhận `lang` từ `useTranslation()` của chính component gọi, KHÔNG đọc `i18n.language`
 * ở tầng module — đọc ở tầng module thì giá trị đóng băng theo lúc tải trang.
 */
export const pickPromoText = (
  lang: string | undefined,
  vi?: string | null,
  en?: string | null,
): string => ((lang || "vi").startsWith("en") && en ? en : vi ?? "");

// Render text có markup **in đậm** thành các span (đậm/thường), giữ nguyên style cha (inherit)
export const RichText = ({ text }: { text: string }) => (
  <>
    {text
      .split(/(\*\*[^*]+\*\*)/g)
      .filter(Boolean)
      .map((part, i) => {
        const bold = part.startsWith("**") && part.endsWith("**");
        return (
          <Text
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            span
            inherit
            fw={bold ? 700 : undefined}
          >
            {bold ? part.slice(2, -2) : part}
          </Text>
        );
      })}
  </>
);

const useStyles = createStyles(() => ({
  // Header title — Figma: Roboto Bold 700 / 18px / 26px / #0E121B
  headerTitle: {
    fontFamily: "Roboto",
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "26px",
    color: "#0E121B",
  },
  // Section title — Figma: Roboto Bold 700 / 24px / 32px / #0171BB
  sectionTitle: {
    fontFamily: "Roboto",
    fontSize: 24,
    fontWeight: 700,
    lineHeight: "32px",
    color: "#0171BB",
    marginBottom: 16,
  },
  // Card — Figma: white, radius 16, shadow 0 2px 15px rgba(0,0,0,0.04), no border
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    boxShadow: "0px 2px 15px 0px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    cursor: "pointer",
    transition: "transform 150ms ease, box-shadow 150ms ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0px 8px 20px 0px rgba(16, 24, 40, 0.10)",
    },
  },
  // Card title — Figma 16px / line-height 19px / #0E121B (weight 800 theo yêu cầu Justin)
  cardTitle: {
    fontFamily: "Roboto",
    fontSize: 16,
    fontWeight: 800,
    lineHeight: "19px",
    color: "#0E121B",
  },
  // Meta (date / location) — Figma: Roboto Regular 400 / 14px / line-height 16px / #6C727F
  metaText: {
    fontFamily: "Roboto",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "16px",
    color: "#6C727F",
  },
  // Separator between start/end date — Figma Line 79: width 4, border-top 1px #CDCDCD
  dateSeparator: {
    width: 4,
    height: 0,
    borderTop: "1px solid #CDCDCD",
    flexShrink: 0,
  },
  // ---- Detail view ----
  detailTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: "#0E121B",
    lineHeight: 1.4,
  },
  dateChip: {
    alignSelf: "flex-start",
    background: "#EAF2FE",
    borderRadius: 8,
    padding: "6px 12px",
  },
  dateChipText: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1D4ED8",
  },
  // Section heading "Mô tả chung" / "Hướng dẫn sử dụng" — Figma: Roboto Bold 16/20 #424242
  sectionHeading: {
    fontFamily: "Roboto",
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "24px",
    color: "#424242",
  },
  // Body text — Figma: Roboto Regular 14/20 #424242
  bodyText: {
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "24px",
    color: "#424242",
  },
  // Bullet dot — Figma: 6x6 #424242
  bullet: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: "#424242",
    flexShrink: 0,
    marginTop: 9,
  },
}));

const PromotionCard = ({
  item,
  onClick,
}: {
  item: Promotion;
  onClick: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const { classes } = useStyles();
  const cardTitle = pickPromoText(i18n.language, item.title, item.titleEn);
  const cardImage = pickPromoText(
    i18n.language,
    item.imageRepresent,
    item.imageRepresentEn,
  );
  return (
    <Card p={0} className={classes.card} onClick={onClick}>
      {/* Ảnh — Figma: 386x227, border-radius 15px (bo đủ 4 góc) */}
      <Box
        style={{ height: 227, borderRadius: 15, overflow: "hidden" }}
      >
        <Image
          src={cardImage || imgKmFallback}
          alt={cardTitle}
          height={227}
          fit="cover"
          withPlaceholder
          styles={{
            root: { height: "100%" },
            figure: { height: "100%" },
            imageWrapper: { height: "100%" },
            placeholder: { height: "100%" },
            image: { height: "100% !important", objectFit: "cover" },
          }}
        />
      </Box>
      <Stack spacing={8} p={12}>
        <Text className={classes.cardTitle} fw={800} lineClamp={2}>
          {cardTitle}
        </Text>
        <Flex align="center" gap={4}>
          <CalendarCheckIcon style={{ flexShrink: 0 }} />
          <Text className={classes.metaText}>{item.startDate}</Text>
          {item.endDate && <Box className={classes.dateSeparator} />}
          <Text className={classes.metaText}>{item.endDate}</Text>
        </Flex>
        <Flex align="center" gap={4}>
          <Map02Icon style={{ flexShrink: 0 }} />
          <Text className={classes.metaText}>
            {pickPromoText(i18n.language, item.locationName, item.locationNameEn) ||
              item.merchantName ||
              t("NATIONWIDE")}
          </Text>
        </Flex>
      </Stack>
    </Card>
  );
};

const PromotionSection = ({
  title,
  items,
  emptyText,
  onSelect,
}: {
  title: string;
  items: Promotion[];
  emptyText: string;
  onSelect: (item: Promotion) => void;
}) => {
  const { classes } = useStyles();
  return (
    <div>
      <Text className={classes.sectionTitle}>{title}</Text>
      {items.length === 0 ? (
        <Text className={classes.metaText} py="sm">
          {emptyText}
        </Text>
      ) : (
        <Grid gutter={24}>
          {items.map((item) => (
            <Grid.Col span={4} key={item.pmId}>
              <PromotionCard item={item} onClick={() => onSelect(item)} />
            </Grid.Col>
          ))}
        </Grid>
      )}
    </div>
  );
};

const PromotionDetail = ({ item }: { item: Promotion }) => {
  const { t, i18n } = useTranslation();
  const { classes } = useStyles();
  const detailTitle = pickPromoText(i18n.language, item.title, item.titleEn);
  const detailImage = pickPromoText(
    i18n.language,
    item.imageRepresent,
    item.imageRepresentEn,
  );
  // Hướng dẫn sử dụng: mỗi ý 1 dòng (tách theo xuống dòng; không dùng '*' vì '**' là markup in đậm)
  const guideItems = pickPromoText(i18n.language, item.guide, item.guideEn)
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const description =
    pickPromoText(i18n.language, item.content, item.contentEn) ||
    pickPromoText(i18n.language, item.shortContent, item.shortContentEn) ||
    "";
  return (
    <Stack spacing={32} pt={16}>
      {/* Top — desktop: ảnh bên trái, thông tin bên phải */}
      <Grid gutter={40}>
        <Grid.Col
          span={5}
          style={{ display: "flex", alignItems: "center" }}
        >
          <Image
            src={detailImage || imgKmFallback}
            alt={detailTitle}
            height={300}
            radius="md"
            fit="cover"
            withPlaceholder
            style={{ width: "100%" }}
          />
        </Grid.Col>
        <Grid.Col span={7}>
          <Stack spacing={16} h="100%" justify="center">
            <Text className={classes.detailTitle}>{detailTitle}</Text>
            <Flex align="center" gap={8} className={classes.dateChip}>
              <IconCalendar size={16} color="#1D4ED8" />
              <Text className={classes.dateChipText}>
                {item.startDate} - {item.endDate}
              </Text>
            </Flex>
            <Flex align="center" gap={8}>
              <IconMapPin size={16} color="#6C727F" />
              <Text className={classes.metaText}>
                {pickPromoText(
                  i18n.language,
                  item.locationName,
                  item.locationNameEn,
                ) ||
                  item.merchantName ||
                  t("NATIONWIDE")}
              </Text>
            </Flex>
          </Stack>
        </Grid.Col>
      </Grid>

      <Divider />

      {description || guideItems.length > 0 ? (
        <Stack spacing={32}>
          {/* Mô tả chung */}
          {description && (
            <Stack spacing={12}>
              <Text className={classes.sectionHeading}>
                {t("PROMOTION_DESCRIPTION")}
              </Text>
              <Text className={classes.bodyText}>
                <RichText text={description} />
              </Text>
            </Stack>
          )}
          {/* Hướng dẫn sử dụng */}
          {guideItems.length > 0 && (
            <Stack spacing={12}>
              <Text className={classes.sectionHeading}>
                {t("PROMOTION_GUIDE")}
              </Text>
              <Stack spacing={12}>
                {guideItems.map((line) => (
                  <Flex key={line} gap={8} align="flex-start">
                    <Box className={classes.bullet} />
                    <Text className={classes.bodyText}>
                      <RichText text={line} />
                    </Text>
                  </Flex>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      ) : (
        <Text className={classes.metaText}>{t("NOT_FOUND_DATA")}</Text>
      )}
    </Stack>
  );
};

// Modal đứng riêng hiển thị "Chi tiết chương trình" — dùng cho cả prelogin (mục DÀNH RIÊNG CHO BẠN)
export const PromotionDetailModal = ({
  opened,
  item,
  close,
}: {
  opened: boolean;
  item: Promotion | null;
  close: () => void;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  return (
    <Modal
      opened={opened}
      onClose={close}
      size={1488}
      radius={16}
      centered
      closeButtonProps={{ iconSize: 16 }}
      title={
        <Text className={classes.headerTitle}>
          {t("PROMOTION_DETAIL_TITLE")}
        </Text>
      }
      styles={{
        header: {
          padding: "20px 24px",
          marginBottom: 0,
          borderBottom: "1px solid #E1E4EA",
        },
        close: {
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "#F5F7FA",
        },
        body: { padding: 32 },
        // Bo 16px cả 4 góc — clip nội dung bên trong
        content: { borderRadius: 16, overflow: "hidden" },
      }}
    >
      {item && <PromotionDetail item={item} />}
    </Modal>
  );
};

const PromotionModal = ({ opened, close }: Props) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [selected, setSelected] = useState<Promotion | null>(null);

  const { data: partnerData } = useSearchPromotionQuery({
    categoryCode: "ALL",
    pageNumber: 1,
  });
  const { data: exclusiveData } = useSearchPromotionQuery({
    categoryCode: "SPECIFIC",
    pageNumber: 1,
  });

  const partnerOffers = get(partnerData, "body.promotions", []) as Promotion[];
  const exclusiveOffers = get(
    exclusiveData,
    "body.promotions",
    [],
  ) as Promotion[];

  const handleClose = () => {
    setSelected(null);
    close();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={selected ? 1488 : 1240}
      radius={16}
      centered
      closeButtonProps={{ iconSize: 16 }}
      title={
        selected ? (
          <Flex align="center" gap={8}>
            <ActionIcon variant="subtle" onClick={() => setSelected(null)}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Text className={classes.headerTitle}>
              {t("PROMOTION_DETAIL_TITLE")}
            </Text>
          </Flex>
        ) : (
          <Text className={classes.headerTitle}>{t("PROMOTION_TITLE")}</Text>
        )
      }
      styles={{
        // Header — Figma: padding 20/24, bottom border #E1E4EA
        header: {
          padding: "20px 24px",
          marginBottom: 0,
          borderBottom: "1px solid #E1E4EA",
        },
        // Close — Figma: 32x32 circle, #F5F7FA
        close: {
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "#F5F7FA",
        },
        // Body — Figma: padding 20
        body: {
          padding: 20,
        },
        // Bo 16px cả 4 góc — clip nội dung bên trong
        content: { borderRadius: 16, overflow: "hidden" },
      }}
    >
      {selected ? (
        <PromotionDetail item={selected} />
      ) : (
        <Stack spacing={20}>
          <PromotionSection
            title={t("PARTNER_OFFERS")}
            items={partnerOffers}
            emptyText={t("NOT_FOUND_DATA")}
            onSelect={setSelected}
          />
          <PromotionSection
            title={t("EXCLUSIVE_FOR_YOU")}
            items={exclusiveOffers}
            emptyText={t("NOT_FOUND_DATA")}
            onSelect={setSelected}
          />
        </Stack>
      )}
    </Modal>
  );
};

export default PromotionModal;
